import React from "react";
import { TextField } from "@bale/ui";
import Modal from "components/commons/modal/Modal";
import { strings } from "intl/fa";
import styles from "./BonusCodeInputModal.module.scss";
import { useInputHandler } from "hooks/useInputHandler";
import { inquireBonusCodeThunk } from "core/code/code.thunk";
import { useRequest } from "hooks/useRequest";
import {
  ApiBonusCodeRecord,
  BonusCodeCondition,
  BonusCodeState,
  BonusCodeType,
} from "core/code/code.entity";
import { toast } from "react-toastify";
import { formatStrings } from "utils/utils";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import { GetAdPriceResponse } from "core/adPrice/adPrice.api";
import { useAppSelector } from "utils/store";
import { normalizeCode } from "../../../utils/bonusCode";
import styled from "@emotion/styled";
import { AdSpot, ApiAdRecord } from "core/orderAd/orderAd.entity";

const CodeTextField = styled(TextField)<{ hasContent: boolean }>`
  & input {
    direction: ${({ hasContent }) => (hasContent ? "ltr" : "rtl")} !important;
  }
`;

interface Props {
  onClose?: () => void;
  onSubmit?: (confirmed: boolean) => void;
  adRecord: ApiAdRecord & { bonusView?: number; bonusClick?: number };
  adPrice: GetAdPriceResponse | undefined;
  setBonusCode: (code: ApiBonusCodeRecord) => void;
}

const BonusCodeInputModal: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { loading, sendRequest } = useRequest(inquireBonusCodeThunk);
  const codeInput = useInputHandler({
    showDigitsLatin: true,
    beforeChange: normalizeCode,
  });
  let selectedTime = useAppSelector((state) => state.periodCapacityData.selectedTime);
  // TODO: removes this after moving errors from getPaymentToken to inquireBonusCode
  let selectedOrderSpot = useAppSelector((state) => state.ordersList.selectedOrder?.spot);

  function inquireBonusCode() {
    // TODO: removes this after moving errors from getPaymentToken to inquireBonusCode
    if (
      (codeInput.rawValue === "TESTEYD" || codeInput.rawValue === "BALE_EYEDFETR100") &&
      selectedOrderSpot === AdSpot.AdSpotSponsoredMessage
    ) {
      toast.info(strings.bonus_code_errors.sponsored_wrong_ad_placement);
      return;
    }

    sendRequest({
      code: codeInput.rawValue.trim(),
      onSuccess: (code) => {
        // response-based error handling:
        if (code.state === BonusCodeState.Used) {
          toast.info(strings.bonus_code_errors.used);
          return;
        }
        if (code.state === BonusCodeState.Expired) {
          toast.info(strings.bonus_code_errors.expired);
          return;
        }
        const time_period_start = new Date(code.time_period_start).getTime();
        const time_period_end = new Date(code.time_period_end).getTime();
        const adtime = selectedTime.date + selectedTime.hour * 60 * 60 * 1000;

        if (
          code.condition === BonusCodeCondition.TimePeriod &&
          (time_period_start > adtime || time_period_end < adtime)
        ) {
          toast.info(strings.bonus_code_errors.order_date_not_in_period);
          return;
        }
        const viewNumber = props.adRecord.set_view || props.adRecord.view_count;
        const isView = Boolean(viewNumber);
        if (
          isView
            ? code.type === BonusCodeType.ClickFixed || code.type === BonusCodeType.ClickPercent
            : code.type === BonusCodeType.ViewFixed || code.type === BonusCodeType.ViewPercent
        ) {
          toast.info(
            formatStrings(strings.bonus_code_errors.wrong_type, {
              type: isView ? strings.bonus_code_errors.clicky : strings.bonus_code_errors.viewy,
            })
          );
          return;
        }
        if (code.price_threshold > props.adPrice?.total_price! || 0) {
          toast.info(
            formatStrings(strings.bonus_code_errors.price_threshold_not_met, {
              threshold: digitsToHindi(toMoneyFormat(String(code.price_threshold / 10))),
            })
          );
          return;
        }

        // Now we know code is OK:
        code && props.setBonusCode(code);
        props.onClose?.();
      },
    });
  }

  return (
    <>
      <Modal
        title={strings.bonus_code_input.title}
        onClose={props.onClose}
        primaryButton={{
          label: strings.bonus_code_input.submit_code,
          disable: !codeInput.rawValue,
          onClick: inquireBonusCode,
        }}
        mobileBottomSheet={true}
        primaryLoading={loading}
      >
        <div className={styles.Container}>
          <div className={styles.Hint}>{strings.bonus_code_input.hint}</div>
          <CodeTextField
            {...codeInput}
            hasContent={Boolean(codeInput.value)}
            placeholder={strings.bonus_code_input.placeholder}
            dir="rtl"
            label={strings.bonus_code_input.title}
          />
        </div>
      </Modal>
    </>
  );
};

export default BonusCodeInputModal;
